import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminSidebar from '../../components/SuperAdmin/SuperAdminSidebar';
import { API_ENDPOINTS } from '../../config/api';
import '../../styles/Dashboard.css';
import '../../styles/SuperAdmin/TrialUsersManagement.css';

const formatDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString();
};

const TrialUsersManagement = () => {
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTrialUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const token = sessionStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS_TRIAL_USERS, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to fetch trial users');
      }

      setRecords(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch trial users');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user.role !== 'super_admin') {
      navigate('/');
      return;
    }

    fetchTrialUsers();
  }, [user.role, navigate, fetchTrialUsers]);

  const filteredRecords = useMemo(() => {
    if (!searchTerm.trim()) return records;
    const key = searchTerm.toLowerCase();

    return records.filter((item) => {
      const adminName = item?.adminUserId?.name || '';
      const adminEmail = item?.adminUserId?.email || '';
      const companyName = item?.companyId?.name || '';
      const planName = item?.planId?.name || '';
      const mobile = item?.mobileNumber || '';

      return [adminName, adminEmail, companyName, planName, mobile]
        .some((value) => String(value).toLowerCase().includes(key));
    });
  }, [records, searchTerm]);

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <SuperAdminSidebar user={user} />
        <div className="dashboard-main">
          <div className="trial-users-loading">Loading trial users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <SuperAdminSidebar user={user} />
      <div className="dashboard-main">
        <div className="trial-users-header">
          <h1>Trial Users OTP Listing</h1>
          <p>Users who generated OTP but payment is not completed yet.</p>
        </div>

        <div className="trial-users-toolbar">
          <input
            type="text"
            placeholder="Search by mobile, admin, company, plan"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="button" onClick={fetchTrialUsers}>Refresh</button>
        </div>

        {error ? <div className="trial-users-error">{error}</div> : null}

        <div className="trial-users-table-wrap">
          <table className="trial-users-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Mobile</th>
                <th>OTP Verified</th>
                <th>Plan</th>
                <th>Company</th>
                <th>Admin</th>
                <th>Generated At</th>
                <th>Verified At</th>
                <th>SMS</th>
                <th>Attempts</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={10} className="trial-users-empty">No trial users found.</td>
                </tr>
              ) : (
                filteredRecords.map((item, idx) => (
                  <tr key={item._id || `${item.mobileNumber}-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{item.mobileNumber}</td>
                    <td>
                      <span className={`badge ${item.otpVerified ? 'ok' : 'pending'}`}>
                        {item.otpVerified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td>{item?.planId?.name || '-'}</td>
                    <td>{item?.companyId?.name || '-'}</td>
                    <td>{item?.adminUserId?.name || item?.adminUserId?.email || '-'}</td>
                    <td>{formatDateTime(item.otpGeneratedAt || item.createdAt)}</td>
                    <td>{formatDateTime(item.otpVerifiedAt)}</td>
                    <td>
                      <span className={`badge ${item.smsDelivered ? 'ok' : 'pending'}`}>
                        {item.smsDelivered ? 'Delivered' : 'Not delivered'}
                      </span>
                    </td>
                    <td>{Number(item.otpAttempts || 0)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrialUsersManagement;
